import React, {
    useState, useEffect, useRef, useMemo
} from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Text, Box, Divider, Grid
} from 'components/ui';
import localeUtils from 'utils/LanguageLocale';
import {
    renderTopCell,
    renderPriceCell,
    renderRatingCell,
    renderAttributeCell
} from 'components/ProductPage/ProductCompareTool/ProductCompareToolItem/ProductCompareToolItem';
import { site, space } from 'style/config';
import ProductCompareToolEN from 'components/ProductPage/ProductCompareTool/locales/ProductCompareTool_en_US.js';
import ProductCompareToolFR from 'components/ProductPage/ProductCompareTool/locales/ProductCompareTool_fr_CA.js';
import pdpCompareUtils from 'utils/pdpCompare';

const ProductCompareTool = props => {
    const {
        podId, skuList, title, resultId, totalResults, isCarousel, similarProductVariants, isLoading, pdpCompareAttributes, currentProduct
    } =
        props;

    const getText = localeUtils.getLocalisedDataFromResource(ProductCompareToolEN, ProductCompareToolFR);

    const [recommendedSkus, setRecommendedSkus] = useState([]);
    const scrollRef = useRef(null);

    const transformedProduct = useMemo(() => pdpCompareUtils.transformProductDataToRecsServiceFormat(currentProduct), [currentProduct]);

    useEffect(() => {
        if (podId && skuList && skuList.length > 0) {
            const skusToUse = skuList.length === 6 ? skuList.slice(0, 5) : skuList;
            const finalSkus = [transformedProduct, ...skusToUse];

            setRecommendedSkus(finalSkus);

            if (scrollRef.current) {
                scrollRef.current.scrollLeft = 0;
            }
        } else {
            setRecommendedSkus([]);
        }
    }, [podId, skuList, transformedProduct]);

    // Filter pdpCompareAttributes to only include those present in some of the sku facets or 'price' and 'rating'
    const filteredPdpCompareAttributes = useMemo(() => {
        const facetNames = new Set();

        // Include facets from the transformed product (first column)
        transformedProduct?.facets?.forEach(facet => {
            if (facet.name) {
                facetNames.add(facet.name);
            }
        });

        // Include facets from skuList
        skuList?.forEach(sku => {
            sku?.facets?.forEach(facet => {
                if (facet.name) {
                    facetNames.add(facet.name);
                }
            });
        });

        const attributesToFilter = pdpCompareAttributes?.length > 0 ? pdpCompareAttributes : ['price', 'rating'];

        const filtered = attributesToFilter.filter(attribute => {
            //Attribute is static
            if (attribute === 'price' || attribute === 'rating') {
                return true;
            }

            //Attribute is dynamic string, check if present in facet names
            if (typeof attribute === 'string') {
                return facetNames.has(attribute);
            }

            //Attribute is dynamic object extract values string
            if (typeof attribute === 'object' && attribute !== null) {
                const attributeValues = Object.values(attribute)?.[0] || [];

                return attributeValues.some(attValue => facetNames.has(attValue));
            }

            return false;
        });

        return filtered;
    }, [pdpCompareAttributes, skuList, transformedProduct]);

    // Calculate grid config: always render 6 columns to fill container width
    const gridConfig = useMemo(() => {
        const usedColumnCount = Math.min(recommendedSkus?.length || 0, 6);
        const columnWidth = site.containerMax / 6;
        const widthPx = columnWidth * (usedColumnCount || 1);

        return {
            columnCount: usedColumnCount,
            columns: `repeat(${usedColumnCount || 1}, ${columnWidth}px)`,
            widthPx
        };
    }, [recommendedSkus?.length]);

    return (
        <Box
            lineHeight='tight'
            marginBottom={[6, 7]}
        >
            <Divider />
            <Text
                is='h2'
                marginY={4}
                fontSize={['md', 'lg']}
                fontWeight='bold'
                children={title}
            />
            {similarProductVariants}
            {isLoading ? null : recommendedSkus?.length ? (
                <Box
                    ref={scrollRef}
                    css={{
                        overflowX: 'auto',
                        width: '100%',
                        scrollBehavior: 'smooth',
                        scrollbarWidth: 'none',
                        overscrollBehaviorX: 'none',
                        '&::-webkit-scrollbar': { display: 'none' }
                    }}
                >
                    <Grid
                        columns={gridConfig.columns}
                        fontSize={['sm', 'base']}
                        gap={null}
                        css={{
                            minWidth: gridConfig.columnCount < 6 ? `${gridConfig.widthPx}px` : '100%',
                            scrollSnapType: 'x mandatory',
                            [`& > *:nth-of-type(-n + ${gridConfig.columnCount})`]: {
                                scrollSnapAlign: 'start'
                            }
                        }}
                        data-cnstrc-recommendations
                        data-cnstrc-recommendations-pod-id={podId}
                        data-cnstrc-result-id={resultId}
                        data-cnstrc-num-results={totalResults}
                    >
                        {/* Row 1: Top cells (product images, brand, name) */}
                        {recommendedSkus.map((sku, index) => (
                            <React.Fragment key={sku?.skuId || index}>
                                {renderTopCell({
                                    sku,
                                    index,
                                    podId,
                                    isCarousel
                                })}
                            </React.Fragment>
                        ))}

                        {/* Subsequent rows: Dynamic attributes */}
                        {filteredPdpCompareAttributes.map((attribute, attrIndex) => {
                            const attributeKey = typeof attribute === 'string' ? attribute : JSON.stringify(attribute);

                            if (attribute === 'price') {
                                return recommendedSkus.map((sku, index) => (
                                    <React.Fragment key={`${attributeKey}-${sku?.skuId || index}`}>
                                        {renderPriceCell({
                                            sku,
                                            index
                                        })}
                                    </React.Fragment>
                                ));
                            }

                            if (attribute === 'rating') {
                                const anySkuHasReviews = recommendedSkus.some(sku => sku?.productReviewCount > 0);

                                if (!anySkuHasReviews) {
                                    return null;
                                }

                                return recommendedSkus.map((sku, index) => (
                                    <React.Fragment key={`${attributeKey}-${sku?.skuId || index}`}>
                                        {renderRatingCell({
                                            sku,
                                            index
                                        })}
                                    </React.Fragment>
                                ));
                            }

                            const cellResults = recommendedSkus.map((sku, index) =>
                                renderAttributeCell({
                                    sku,
                                    index,
                                    attribute,
                                    attrIndex
                                })
                            );

                            const allCellsAreEmpty = cellResults?.every(result => result?.isCellEmpty);

                            if (allCellsAreEmpty) {
                                return null;
                            }

                            return cellResults.map((result, index) => (
                                <React.Fragment key={`${attributeKey}-${recommendedSkus[index]?.skuId || index}`}>{result.cell}</React.Fragment>
                            ));
                        })}
                    </Grid>
                </Box>
            ) : (
                <Text css={styles.noResults}>{getText('noProducts')}</Text>
            )}
        </Box>
    );
};

const styles = {
    noResults: {
        marginTop: space[4],
        display: 'inline-block',
        paddingLeft: space[1]
    }
};

export default wrapFunctionalComponent(ProductCompareTool, 'ProductCompareTool');
